import React, { useEffect, useState } from 'react';
import { IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonItem, IonLabel, IonInput, IonButton, IonButtons, IonBackButton, IonList, IonNote, IonSegment, IonSegmentButton, IonToggle, useIonToast } from '@ionic/react';
import { getApiBaseUrl, setApiBaseUrlOverride, clearApiCache, getApiEnvironment, setApiEnvironment, ApiEnvironment } from '../api';

const DeveloperSettings: React.FC = () => {
  const [current, setCurrent] = useState<string>('');
  const [inputUrl, setInputUrl] = useState<string>('');
  const [isDev, setIsDev] = useState<boolean>(false);
  const [env, setEnv] = useState<ApiEnvironment>('dev');
  const [notificationsEnabled, setNotificationsEnabled] = useState<boolean>(false);
  const [presentToast] = useIonToast();

  useEffect(() => {
    try {
      const meRaw = window.localStorage.getItem('me');
      if (meRaw) {
        const me = JSON.parse(meRaw);
        setIsDev(!!me?.developer);
      }
    } catch {}
    const base = getApiBaseUrl();
    const currentEnv = getApiEnvironment();
    setEnv(currentEnv);
    setCurrent(base);
    setInputUrl(base);
    
    // Kontrollera notifikationstillstånd
    if ('Notification' in window) {
      setNotificationsEnabled(Notification.permission === 'granted');
    }
  }, []);

  const save = () => {
    if (!isDev) return;
    setApiBaseUrlOverride(inputUrl);
    clearApiCache();
    const newBase = getApiBaseUrl();
    setCurrent(newBase);
  };

  const reset = () => {
    if (!isDev) return;
    setApiBaseUrlOverride('');
    clearApiCache();
    const newBase = getApiBaseUrl();
    setCurrent(newBase);
    setInputUrl(newBase);
  };

  const handleEnvChange = (value: string) => {
    if (!isDev) return;
    const newEnv: ApiEnvironment = value === 'prod' ? 'prod' : 'dev';
    setApiEnvironment(newEnv);
    setEnv(newEnv);
    // After env change recalc current base and input
    const base = getApiBaseUrl();
    setCurrent(base);
    // Only auto-fill input when switching to dev (so you can override). For prod we leave previous dev override visible but not used.
    if (newEnv === 'dev') {
      setInputUrl(base);
    }
  };

  const handleNotificationToggle = async (checked: boolean) => {
    if (checked) {
      // Aktivera notifikationer
      try {
        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
          localStorage.removeItem('notificationPromptDeclined');
          setNotificationsEnabled(true);
          presentToast({
            message: 'Notifikationer aktiverade',
            duration: 2000,
            color: 'success'
          });
          // Ladda om sidan för att trigga FCM token-registrering
          setTimeout(() => window.location.reload(), 1000);
        } else {
          setNotificationsEnabled(false);
          presentToast({
            message: 'Notifikationer nekades',
            duration: 2000,
            color: 'warning'
          });
        }
      } catch (error) {
        console.error('Fel vid aktivering av notifikationer:', error);
        setNotificationsEnabled(false);
      }
    } else {
      // Avaktivera notifikationer (markera som declined)
      localStorage.setItem('notificationPromptDeclined', 'true');
      setNotificationsEnabled(false);
      presentToast({
        message: 'Du kommer inte frågas om notifikationer igen. Ändra i webbläsarens inställningar för att återaktivera.',
        duration: 4000,
        color: 'medium'
      });
    }
  };

  if (!isDev) {
    return (
      <IonPage placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <IonHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonToolbar color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonButtons slot="start" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}><IonBackButton /></IonButtons>
            <IonTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Utvecklarläge</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonNote placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Du är inte markerad som utvecklare.</IonNote>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
      <IonHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <IonToolbar color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonButtons slot="start" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}><IonBackButton /></IonButtons>
          <IonTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Utvecklarläge</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <IonList placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <h2>Push-notifikationer</h2>
              <p>Aktivera för att få meddelanden</p>
            </IonLabel>
            <IonToggle 
              checked={notificationsEnabled} 
              onIonChange={(e) => handleNotificationToggle(e.detail.checked)}
              disabled={!('Notification' in window)}
              placeholder={undefined}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
          </IonItem>
          <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonLabel position="stacked" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Miljö</IonLabel>
            <IonSegment value={env} onIonChange={e => handleEnvChange(e.detail.value!)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonSegmentButton value="dev" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>DEV</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton value="prod" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>PROD</IonLabel>
              </IonSegmentButton>
            </IonSegment>
          </IonItem>
          <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonLabel position="stacked" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Nuvarande API-bas-URL</IonLabel>
            <IonNote placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>{current}</IonNote>
          </IonItem>
          <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonLabel position="stacked" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Ny API-bas-URL</IonLabel>
            <IonInput value={inputUrl} placeholder="http://localhost:8000" onIonChange={e => setInputUrl(e.detail.value!)} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
          </IonItem>
        </IonList>
        <div style={{marginTop: '1rem', display:'flex', gap:'0.5rem'}}>
          <IonButton onClick={save} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Spara</IonButton>
          <IonButton color="medium" onClick={reset} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Återställ</IonButton>
        </div>
        <IonNote style={{display:'block', marginTop:'1rem'}} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          Ändringen lagras i localStorage och gäller endast för utvecklarkonton i denna webbläsare.
        </IonNote>
      </IonContent>
    </IonPage>
  );
};

export default DeveloperSettings;
